import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, getProductionId } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FileSignature, Plus, Pencil, Trash2, Users, ShieldAlert, CheckCircle2,
  Clock, Download, Eye, BarChart3, Copy, LayoutTemplate,
} from "lucide-react";
import { fmtDate } from "@/lib/format";
import { Progress } from "@/components/ui/progress";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-xs uppercase tracking-widest px-3 h-9 border-b-2 -mb-px transition-colors ${
        active ? "border-gold text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"
      }`}
    >{children}</button>
  );
}

export default function Forms() {
  const { user } = useAuth();
  const productionId = getProductionId();
  const isManager = ["org_admin", "super_admin"].includes(user?.role);
  const nav = useNavigate();

  const [tab, setTab] = useState(isManager ? "manage" : "mine");
  const [forms, setForms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [submissionsByForm, setSubmissionsByForm] = useState({}); // { form_id: [subs] }
  const [openFormId, setOpenFormId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!productionId) return;
    setLoading(true);
    try {
      const params = { production_id: productionId };
      if (isManager) params.include_inactive = true;
      const [f, comp, tpl] = await Promise.all([
        api.get("/forms", { params }),
        isManager
          ? api.get("/forms/compliance", { params: { production_id: productionId } })
          : Promise.resolve({ data: [] }),
        isManager
          ? api.get("/form-templates", { params: { production_id: productionId } })
          : Promise.resolve({ data: [] }),
      ]);
      setForms(f.data || []);
      setCompliance(comp.data || []);
      setTemplates(tpl.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [productionId]);

  const loadSubs = async (formId) => {
    const r = await api.get(`/forms/${formId}/submissions`);
    setSubmissionsByForm((s) => ({ ...s, [formId]: r.data }));
  };

  const del = async (form) => {
    if (!confirm(`Delete "${form.title}" and all its submissions?`)) return;
    await api.delete(`/forms/${form.id}`);
    toast.success("Form deleted");
    load();
  };
  const toggleActive = async (form) => {
    await api.patch(`/forms/${form.id}`, { active: !form.active });
    toast.success(form.active ? "Form deactivated" : "Form activated");
    load();
  };

  const myForms = useMemo(() => forms.filter((f) => f.active !== false), [forms]);

  const downloadPdf = async (formId, submissionId) => {
    const r = await api.get(`/forms/${formId}/submissions/${submissionId}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "form.pdf";
    a.click();
    URL.revokeObjectURL(url);
  };

  const applyTemplate = async (templateId) => {
    try {
      const { data } = await api.post(`/form-templates/${templateId}/instantiate`);
      toast.success("Draft form created from template");
      nav(`/forms/${data.id}/edit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create from template");
    }
  };
  const deleteTemplate = async (t) => {
    if (!confirm(`Delete the "${t.title}" template?`)) return;
    await api.delete(`/form-templates/${t.id}`);
    toast.success("Template removed");
    load();
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="forms-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Company</div>
          <h1 className="mt-1 font-display text-4xl tracking-tight font-light flex items-center gap-3">
            <FileSignature className="w-8 h-8 text-gold" /> Forms &amp; Agreements
          </h1>
          <p className="mt-1 text-zinc-400 text-sm max-w-2xl">
            Contracts, policy acknowledgements, and compliance items. Required forms
            block sign-in until they&apos;re signed; the rest sit in the notification bell.
          </p>
        </div>
        {isManager && (
          <button
            data-testid="new-form-btn"
            onClick={() => nav("/forms/new")}
            style={goldBtn}
            className="h-10 px-4 rounded-md font-semibold text-sm hover:brightness-110 inline-flex items-center gap-1.5"
          ><Plus className="w-4 h-4" /> New form</button>
        )}
      </header>

      <div className="border-b border-white/10 flex gap-1">
        {!isManager && <TabBtn testid="tab-mine" active={tab === "mine"} onClick={() => setTab("mine")}>My Forms</TabBtn>}
        {isManager && <TabBtn testid="tab-manage" active={tab === "manage"} onClick={() => setTab("manage")}>Manage</TabBtn>}
        {isManager && <TabBtn testid="tab-templates" active={tab === "templates"} onClick={() => setTab("templates")}>Templates</TabBtn>}
        {isManager && <TabBtn testid="tab-compliance" active={tab === "compliance"} onClick={() => setTab("compliance")}>Compliance</TabBtn>}
        {isManager && <TabBtn testid="tab-mine-mgr" active={tab === "mine"} onClick={() => setTab("mine")}>My Forms</TabBtn>}
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm py-10 text-center">Loading forms…</div>
      ) : tab === "mine" ? (
        <section data-testid="forms-mine">
          {myForms.length === 0 ? (
            <div className="rounded border border-dashed border-white/10 py-12 text-center text-zinc-500 text-sm">
              No forms assigned to you right now.
            </div>
          ) : (
            <div className="grid gap-3">
              {myForms.map((f) => {
                const done = !!f.my_submission_id;
                return (
                  <div key={f.id} className="rounded-lg border border-white/10 bg-[#121212] p-4 flex items-center gap-4" data-testid={`form-card-${f.id}`}>
                    <div className={`w-10 h-10 rounded-md grid place-items-center shrink-0 ${done ? "bg-emerald-500/10 text-emerald-400" : "bg-gold/10 text-gold"}`}>
                      {done ? <CheckCircle2 className="w-5 h-5" /> : <FileSignature className="w-5 h-5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium">{f.title}</div>
                        {f.is_required && (
                          <span className="text-[10px] uppercase tracking-widest text-amber-300 border border-amber-400/30 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                            <ShieldAlert className="w-3 h-3" /> Required
                          </span>
                        )}
                      </div>
                      {f.description && <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{f.description}</div>}
                      {done && <div className="text-[11px] text-zinc-500 mt-1"><Clock className="w-3 h-3 inline mr-1" />Signed {fmtDate(f.my_submitted_at)}</div>}
                    </div>
                    {done ? (
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`download-mine-${f.id}`}
                          onClick={() => downloadPdf(f.id, f.my_submission_id)}
                          className="text-xs inline-flex items-center gap-1.5 h-9 px-3 rounded border border-white/10 hover:bg-white/5"
                        ><Download className="w-3.5 h-3.5" /> PDF</button>
                        <Link
                          to={`/forms/${f.id}/fill`}
                          data-testid={`re-sign-${f.id}`}
                          className="text-xs h-9 px-3 rounded border border-white/10 hover:bg-white/5 inline-flex items-center"
                        >Update</Link>
                      </div>
                    ) : (
                      <Link
                        to={`/forms/${f.id}/fill`}
                        data-testid={`fill-${f.id}`}
                        style={goldBtn}
                        className="text-xs h-9 px-4 rounded font-semibold hover:brightness-110 inline-flex items-center gap-1.5"
                      >Fill &amp; sign</Link>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      ) : tab === "manage" ? (
        <section data-testid="forms-manage">
          {forms.length === 0 ? (
            <div className="rounded border border-dashed border-white/10 py-12 text-center text-zinc-500 text-sm">
              No forms yet. Click <b>New form</b> to build your first agreement.
            </div>
          ) : (
            <div className="rounded-lg border border-white/10 overflow-hidden">
            {/* Horizontal scroll wrapper — the 6-col table doesn't fit on
                phones, so we let it scroll horizontally instead of squishing
                every column into an unreadable strip. */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-[#0A0A0A] text-zinc-500 text-[10px] uppercase tracking-[0.2em]">
                  <tr>
                    <th className="text-left p-3">Title</th>
                    <th className="text-left p-3">Assignment</th>
                    <th className="text-left p-3">Fields</th>
                    <th className="text-left p-3">Submissions</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {forms.map((f) => (
                    <tr key={f.id} className="border-t border-white/5" data-testid={`manage-row-${f.id}`}>
                      <td className="p-3">
                        <div className="font-medium flex items-center gap-2">
                          {f.title}
                          {f.is_required && (
                            <span className="text-[10px] uppercase tracking-widest text-amber-300 border border-amber-400/30 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                              <ShieldAlert className="w-3 h-3" /> Required
                            </span>
                          )}
                        </div>
                        {f.description && <div className="text-xs text-zinc-500 mt-0.5 line-clamp-1 max-w-md">{f.description}</div>}
                      </td>
                      <td className="p-3 text-xs text-zinc-400">
                        {f.assignment?.scope === "all" ? "Everyone" :
                          f.assignment?.scope === "roles" ? (f.assignment.roles || []).map(r => r.replace("_", " ")).join(", ") :
                          `${(f.assignment?.user_ids || []).length} users`}
                      </td>
                      <td className="p-3 text-xs text-zinc-400">{(f.fields || []).length}</td>
                      <td className="p-3 text-xs text-zinc-400">
                        <button
                          data-testid={`view-subs-${f.id}`}
                          onClick={async () => {
                            if (openFormId === f.id) { setOpenFormId(null); return; }
                            if (!submissionsByForm[f.id]) await loadSubs(f.id);
                            setOpenFormId(f.id);
                          }}
                          className="inline-flex items-center gap-1 hover:text-white"
                        ><Eye className="w-3 h-3" /> {f.submission_count || 0}</button>
                      </td>
                      <td className="p-3">
                        <button
                          data-testid={`toggle-active-${f.id}`}
                          onClick={() => toggleActive(f)}
                          className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${f.active ? "border-emerald-400/30 text-emerald-300" : "border-white/10 text-zinc-500"}`}
                        >{f.active ? "Active" : "Inactive"}</button>
                      </td>
                      <td className="p-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <Link
                            to={`/forms/${f.id}/edit`}
                            data-testid={`edit-form-${f.id}`}
                            className="p-1.5 rounded text-zinc-400 hover:text-gold hover:bg-white/5"
                            title="Edit"
                          ><Pencil className="w-3.5 h-3.5" /></Link>
                          <button
                            data-testid={`delete-form-${f.id}`}
                            onClick={() => del(f)}
                            className="p-1.5 rounded text-zinc-500 hover:text-red-400 hover:bg-white/5"
                            title="Delete"
                          ><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              {openFormId && submissionsByForm[openFormId] && (
                <div className="border-t border-white/10 bg-[#0A0A0A] p-4" data-testid={`subs-${openFormId}`}>
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Submissions</div>
                  {submissionsByForm[openFormId].length === 0 ? (
                    <div className="text-xs text-zinc-500">No one has signed this yet.</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="text-zinc-500"><tr>
                        <th className="text-left py-1.5">Name</th>
                        <th className="text-left py-1.5">Email</th>
                        <th className="text-left py-1.5">Signed at</th>
                        <th className="text-right py-1.5">PDF</th>
                      </tr></thead>
                      <tbody>
                        {submissionsByForm[openFormId].map((s) => (
                          <tr key={s.id} className="border-t border-white/5">
                            <td className="py-1.5">{s.user_name}</td>
                            <td className="py-1.5 text-zinc-400">{s.user_email}</td>
                            <td className="py-1.5 text-zinc-400">{fmtDate(s.submitted_at)}</td>
                            <td className="py-1.5 text-right">
                              <button
                                data-testid={`download-sub-${s.id}`}
                                onClick={() => downloadPdf(openFormId, s.id)}
                                className="inline-flex items-center gap-1 text-gold hover:brightness-125"
                              ><Download className="w-3 h-3" /> Download</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}
        </section>
      ) : tab === "compliance" ? (
        <section data-testid="forms-compliance">
          {compliance.length === 0 ? (
            <div className="rounded border border-dashed border-white/10 py-12 text-center text-zinc-500 text-sm">
              Publish an active form to see completion tracking.
            </div>
          ) : (
            <div className="grid gap-4">
              {compliance.map((row) => {
                const pct = Math.round((row.completion_rate || 0) * 100);
                return (
                  <div key={row.form_id} className="rounded-lg border border-white/10 bg-[#121212] p-5" data-testid={`compliance-row-${row.form_id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-medium">{row.title}</div>
                          {row.is_required && (
                            <span className="text-[10px] uppercase tracking-widest text-amber-300 border border-amber-400/30 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                              <ShieldAlert className="w-3 h-3" /> Required
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-zinc-500 mt-1 inline-flex items-center gap-1.5">
                          <Users className="w-3 h-3" /> {row.completed_count} of {row.expected_count} signed
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`font-display text-3xl ${pct === 100 ? "text-emerald-400" : pct > 0 ? "text-gold" : "text-zinc-500"}`}>{pct}%</div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-600">Complete</div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <Progress value={pct} className="h-1.5" data-testid={`compliance-progress-${row.form_id}`} />
                    </div>
                    {row.pending_users.length > 0 && (
                      <div className="mt-4">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
                          <BarChart3 className="w-3 h-3" /> Still waiting on
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {row.pending_users.slice(0, 20).map((u) => (
                            <span key={u.id} className="text-[11px] px-2 py-1 rounded bg-[#0A0A0A] border border-white/10 text-zinc-400" data-testid={`pending-${u.id}`}>
                              {u.name || u.email}
                            </span>
                          ))}
                          {row.pending_users.length > 20 && (
                            <span className="text-[11px] px-2 py-1 rounded text-zinc-500">+{row.pending_users.length - 20} more</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      ) : tab === "templates" ? (
        <section data-testid="forms-templates">
          <div className="text-xs text-zinc-500 mb-3 max-w-2xl">
            Templates are reusable form scaffolds. Save any form as a template, then spin up
            new ones from it in seconds — assignment and required flags stay off by default
            so you can tune them per instance.
          </div>
          {templates.length === 0 ? (
            <div className="rounded border border-dashed border-white/10 py-12 text-center text-zinc-500 text-sm">
              No templates yet. Open any form, click <b>Save as template</b> to reuse it later.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {templates.map((t) => (
                <div
                  key={t.id}
                  data-testid={`template-card-${t.id}`}
                  className="rounded-lg border border-white/10 bg-[#121212] p-4 flex items-start gap-3"
                >
                  <div className="w-9 h-9 rounded-md grid place-items-center shrink-0 bg-gold/10 text-gold">
                    <LayoutTemplate className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{t.title}</div>
                    {t.description && (
                      <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{t.description}</div>
                    )}
                    <div className="text-[11px] text-zinc-600 mt-1">
                      {(t.fields || []).length} field{(t.fields || []).length === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      data-testid={`use-template-${t.id}`}
                      onClick={() => applyTemplate(t.id)}
                      style={goldBtn}
                      className="text-[11px] h-8 px-3 rounded font-semibold hover:brightness-110 inline-flex items-center gap-1"
                    ><Copy className="w-3 h-3" /> Use</button>
                    <button
                      data-testid={`delete-template-${t.id}`}
                      onClick={() => deleteTemplate(t)}
                      className="p-1.5 rounded text-zinc-500 hover:text-red-400 hover:bg-white/5"
                      title="Delete template"
                    ><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
